import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { backendUrl } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Search, Loader2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

import { eur } from "@/lib/format";
const STATI = ["Non pagata", "Pagata", "Parziale"];

export default function Fatture() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const { data } = useQuery({ queryKey: ["fatture"], queryFn: async () => (await api.get("/fatture")).data });
  const { data: clienti } = useQuery({ queryKey: ["clienti"], queryFn: async () => (await api.get("/clienti")).data });
  const [form, setForm] = useState({ cliente_id: "", importo: "", causale: "Consulenza Immigrazione" });
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [cliQuery, setCliQuery] = useState("");
  const [cliOpen, setCliOpen] = useState(false);
  const fatture = data || [];

  const selectedCli = (clienti || []).find((c) => c.id === form.cliente_id);
  const filtered = (clienti || []).filter((c) =>
    `${c.nome} ${c.cognome}`.toLowerCase().includes(cliQuery.trim().toLowerCase())).slice(0, 60);

  const setStato = async (fid, stato_pagamento) => {
    await api.put(`/fatture/${fid}/stato`, { stato_pagamento });
    qc.invalidateQueries({ queryKey: ["fatture"] });
  };

  const resetForm = () => { setForm({ cliente_id: "", importo: "", causale: "Consulenza Immigrazione" }); setCliQuery(""); setCliOpen(false); };

  const create = async () => {
    if (!form.cliente_id || !form.importo) return toast.error("Seleziona cliente e importo");
    setCreating(true);
    try {
      const { data: f } = await api.post("/fatture", form);
      toast.success(`Fattura ${f.numero_completo} generata dal template`);
      setOpen(false); resetForm();
      qc.invalidateQueries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella generazione della fattura");
    } finally { setCreating(false); }
  };

  const remove = async (fid, numero) => {
    if (!window.confirm(`Eliminare la fattura ${numero}? Verrà spostata nel cestino anche su Google Drive.`)) return;
    try {
      await api.delete(`/fatture/${fid}`);
      toast.success(`Fattura ${numero} eliminata`);
      qc.invalidateQueries();
    } catch (e) { toast.error("Errore durante l'eliminazione"); }
  };

  const docUrl = (id) => `${backendUrl}/api/documenti/${id}/download?token=${localStorage.getItem("rb_token")}`;

  const totale = fatture.reduce((n, f) => n + (f.importo || 0), 0);
  const TONO = { Pagata: "text-[#2E5E3A]", "Non pagata": "text-[#8A2E2E]", Parziale: "text-[#9A3B13]" };

  return (
    <div data-testid="fatture-page"
      className="scheda-rb -m-4 sm:-m-5 lg:-m-7 p-4 sm:p-5 lg:p-7 bg-[#F2F1EC] min-h-screen">
      <div className="w-full max-w-[1120px] mx-auto flex flex-col gap-[18px]">

        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex flex-col gap-0.5">
            <h1 className="text-[25px] md:text-[30px] font-bold leading-[1.15] text-[#1B2A4B]">Fatture</h1>
            <p className="text-[14px] md:text-[16px] text-[#4A4A48]" data-testid="fatture-count">
              {fatture.length} document{fatture.length === 1 ? "o" : "i"} · {eur(totale)} fatturati
            </p>
          </div>
          <button data-testid="new-fattura-btn" onClick={() => { resetForm(); setOpen(true); }}
            className="btn btn-g btn-larghezza sm:w-auto"><Plus className="w-4 h-4" /> Nuova fattura</button>
        </header>

        <div className="sez overflow-hidden" data-testid="fatture-elenco">
          <div className="subt">
            <span>Registro</span>
            <span className="text-[12px] font-normal normal-case tracking-normal text-[#5A5A5A]">
              numerazione progressiva per anno
            </span>
          </div>
          {fatture.length === 0 && (
            <div className="riga text-[#5A5A5A]">Nessuna fattura emessa.</div>
          )}
          {fatture.map((f) => (
            <div key={f.id} data-testid="fattura-row" className="riga group gap-4 flex-wrap">
              <span className="w-[92px] shrink-0 text-[#4A4A48]">{f.data}</span>
              <span className="flex-[999_1_200px] min-w-0">
                <span className="block truncate">
                  <b className="text-[#8A6E2F]">{f.numero_completo}</b>
                  <span className="text-[#1A1A1A]"> · {f.cliente_nome}</span>
                </span>
                <span className="block text-[12px] text-[#5A5A5A] truncate">{f.causale}</span>
              </span>
              <span className="w-[110px] shrink-0 text-right text-[15px] font-bold">{eur(f.importo)}</span>
              <span className="w-[130px] shrink-0">
                <select data-testid="invoice-status-toggle" value={f.stato_pagamento} onChange={(e) => setStato(f.id, e.target.value)}
                  className={`campo text-[13px] font-bold py-1.5 ${TONO[f.stato_pagamento] || ""}`}>
                  {STATI.map((st) => <option key={st}>{st}</option>)}
                </select>
              </span>
              <span className="shrink-0 flex items-center gap-2.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                {f.documento_id && (
                  <a href={docUrl(f.documento_id)} target="_blank" rel="noreferrer" title="Scarica il PDF"
                    data-testid={`fattura-download-${f.id}`} className="text-[#5A5A5A] hover:text-[#A8863F]"><FileText className="w-4 h-4" /></a>
                )}
                {can("elimina_fatture") && (
                  <button onClick={() => remove(f.id, f.numero_completo)} title="Elimina fattura"
                    data-testid={`fattura-delete-${f.id}`} className="text-[#8A2E2E]/60 hover:text-[#8A2E2E]"><Trash2 className="w-4 h-4" /></button>
                )}
              </span>
            </div>
          ))}
          <div className="nota-tenue">
            Lo stato si aggiorna a mano solo qui: nelle pratiche si legge dall'incasso collegato.
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="dialogo max-w-md w-full p-5">
            <h3 className="text-[20px] font-bold text-[#1B2A4B] mb-4">Nuova fattura</h3>

            <label className="eti">Cliente</label>
            <div className="relative mt-1 mb-3.5">
              <div className="flex items-center campo gap-2 py-0">
                <Search className="w-4 h-4 text-[#5A5A5A] shrink-0" />
                <input data-testid="fattura-cliente-search"
                  value={selectedCli && !cliOpen ? `${selectedCli.nome} ${selectedCli.cognome}` : cliQuery}
                  onChange={(e) => { setCliQuery(e.target.value); setCliOpen(true); if (form.cliente_id) setForm({ ...form, cliente_id: "" }); }}
                  onFocus={() => setCliOpen(true)} placeholder="Cerca cliente per nome…"
                  className="w-full py-2.5 bg-transparent focus:outline-none text-[14px]" />
                {(selectedCli || cliQuery) && (
                  <button onClick={() => { setForm({ ...form, cliente_id: "" }); setCliQuery(""); setCliOpen(true); }}
                    className="text-[#5A5A5A] hover:text-[#1B2A4B]"><X className="w-4 h-4" /></button>
                )}
              </div>
              {cliOpen && (
                <div className="absolute z-10 mt-1 w-full max-h-52 overflow-y-auto bg-white border border-[#E2E0D9] rounded-[5px] shadow-lg">
                  {filtered.length === 0 && <div className="riga text-[#5A5A5A]">Nessun cliente trovato.</div>}
                  {filtered.map((c) => (
                    <button key={c.id} data-testid={`fattura-cliente-option-${c.id}`}
                      onClick={() => { setForm({ ...form, cliente_id: c.id }); setCliOpen(false); setCliQuery(""); }}
                      className="w-full text-left px-3.5 py-2.5 text-[14px] hover:bg-[#F7F6F2] transition-colors">
                      {c.nome} {c.cognome}{c.is_azienda ? " · Azienda" : ""}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <label className="eti">Importo (€)</label>
            <input data-testid="fattura-importo" type="number" value={form.importo}
              onChange={(e) => setForm({ ...form, importo: e.target.value })} className="campo mt-1 mb-3.5" />
            <label className="eti">Causale</label>
            <input data-testid="fattura-causale" value={form.causale}
              onChange={(e) => setForm({ ...form, causale: e.target.value })} className="campo mt-1 mb-5" />
            <div className="flex justify-end gap-2.5">
              <button onClick={() => { setOpen(false); resetForm(); }} disabled={creating} className="btn btn-o">Annulla</button>
              <button data-testid="fattura-create" onClick={create} disabled={creating} className="btn btn-p">
                {creating && <Loader2 className="w-4 h-4 animate-spin" />} {creating ? "Genero…" : "Genera"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
